import {
  type ButtonOwnProps as MuiButtonOwnProps,
  type PolymorphicProps,
} from '@mui/base'
import { clsx } from 'clsx'
import { type ElementType, type JSX } from 'react'

import { Button, type ButtonOwnProps } from '../Button'
import { CircularProgress } from '../CircularProgress'

export type LoadingButtonOwnProps = {
  /**
   * If `true`, the loading indicator is shown
   * @default false
   */
  loading?: boolean

  /**
   * The loading indicator can be positioned on the start, end, or the center of the button
   * @default center
   */
  loadingPosition?: 'center' | 'end' | 'start'
}

export interface LoadingButtonTypeMap<
  RootComponentType extends ElementType = 'button',
> {
  defaultComponent: RootComponentType
  props: ButtonOwnProps &
    LoadingButtonOwnProps &
    Omit<MuiButtonOwnProps, 'href' | 'rootRef' | 'to' | 'type'>
}

export type LoadingButtonProps<
  RootComponentType extends ElementType =
    LoadingButtonTypeMap['defaultComponent'],
> = PolymorphicProps<LoadingButtonTypeMap<RootComponentType>, RootComponentType>

/**
 * Loading buttons can show loading state and disable interactions.
 */
export const LoadingButton = <RootComponentType extends ElementType = 'button'>(
  props: LoadingButtonProps<RootComponentType>,
): JSX.Element => {
  const {
    children,
    className,
    disabled,
    endIcon,
    loading = false,
    loadingPosition = 'center',
    startIcon,
    ...other
  } = props

  if (process.env.NODE_ENV !== 'production') {
    if (startIcon && loadingPosition !== 'start') {
      console.warn(
        'When startIcon is provided, LoadingPosition should also be "start"',
      )
    }

    if (endIcon && loadingPosition !== 'end') {
      console.warn(
        'When endIcon is provided, LoadingPosition should also be "end"',
      )
    }
  }

  return (
    <Button
      {...other}
      className={clsx('loading-button', className)}
      disabled={loading || disabled}
      endIcon={
        loading && loadingPosition === 'end' ? (
          <CircularProgress className="loading-button__progress loading-button__progress_end" />
        ) : (
          endIcon
        )
      }
      startIcon={
        loading && loadingPosition === 'start' ? (
          <CircularProgress className="loading-button__progress loading-button__progress_start" />
        ) : (
          startIcon
        )
      }
    >
      {loading && loadingPosition === 'center' ? (
        <CircularProgress className="loading-button__progress loading-button__progress_center" />
      ) : null}
      <span
        className={clsx(
          'loading-button__content',
          loading &&
            loadingPosition === 'center' &&
            'loading-button__content_loading',
        )}
      >
        {children}
      </span>
    </Button>
  )
}
